/**
 * components/DataTable
 * ──────────────────────────────────────────────────────────────────────────
 * Every table in SK Company Cash. Generic over the row type, so a column's
 * `render` receives the caller's own row and not `any`.
 *
 * THE COLUMN CONTRACT
 * Columns declare the design's grid tracks verbatim — `{ fr: 1.7 }` is
 * `minmax(0, 1.7fr)`. `resolveTrackWidths` turns those into pixels ONCE per
 * layout and hands the identical array to the header and to every body row,
 * which is what keeps them aligned. The design handoff calls this out
 * explicitly: with bare `fr` tracks, a long holder name widens one body column
 * and not the header, and the two grids visibly desynchronise.
 *
 * BELOW 1024px the table stacks into cards (StackedRow) with the same columns
 * in the same order. A six-column register has no readable line length on a
 * phone, and the design drew no mobile layout, so this is the one place the
 * app makes a layout decision the reference does not contain — deliberately,
 * and in one component rather than in each screen.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useMemo, useState, type ReactNode } from 'react';
import { StyleSheet, Text, View, type LayoutChangeEvent } from 'react-native';
import { neutral } from '../../theme/colors';
import { type as textRole } from '../../theme/typography';
import { resolveTrackWidths, useResponsive } from '../../lib/responsive';
import TableRow from './TableRow';
import StackedRow from './StackedRow';
import type { Column } from './types';

export interface DataTableProps<Row> {
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  keyExtractor: (row: Row, index: number) => string;
  /** Opens the row's detail. Omit to make rows non-interactive. */
  onRowPress?: (row: Row, index: number) => void;
  /**
   * Per-row override — the Approvals table shows "Review ›" on pending rows
   * only, and decided rows must have no affordance at all.
   */
  rowPressable?: (row: Row) => boolean;
  /** Show the `›` chevron on interactive rows. */
  chevron?: boolean;
  /**
   * Row density, matching the three the design actually uses:
   *   compact  11px rows · 15px gutter · 9px header   (register, overview, sync)
   *   default  12px rows · 15px gutter · 9px header   (finance approvals)
   *   roomy    13px rows · 16px gutter · 10px header  (approver console)
   */
  density?: 'compact' | 'default' | 'roomy';
  /** Gap between columns: 12px on the finance tables, 16px on the approver ones. */
  columnGap?: number;
  /**
   * Suppress the column-header row. The Overview's "Needs attention" table has
   * none in the design — the card header ("Needs attention · Worst first") is
   * the only heading it gets.
   */
  hideHeader?: boolean;
  /** Rendered in place of the rows when there are none. */
  empty?: ReactNode;
}

export function DataTable<Row>({
  columns,
  rows,
  keyExtractor,
  onRowPress,
  rowPressable,
  chevron = false,
  density = 'compact',
  columnGap = 12,
  hideHeader = false,
  empty,
}: DataTableProps<Row>) {
  const { stackTables } = useResponsive();
  const [available, setAvailable] = useState(0);

  const onLayout = (event: LayoutChangeEvent) => {
    const next = Math.round(event.nativeEvent.layout.width);
    if (next !== available) setAvailable(next);
  };

  // The three densities, resolved once.
  const metrics =
    density === 'roomy'
      ? { paddingY: 13, gutter: 16, headerPaddingY: 10 }
      : density === 'default'
        ? { paddingY: 12, gutter: 15, headerPaddingY: 9 }
        : { paddingY: 11, gutter: 15, headerPaddingY: 9 };

  const tracks = useMemo(() => columns.map((column) => column.track), [columns]);
  const aligns = useMemo(
    () => columns.map((column) => column.align ?? 'left'),
    [columns],
  );

  /**
   * The chevron lives outside the track list — it is a fixed 18px affordance
   * plus one gap, and folding it into the tracks would make every screen
   * declare a column it never renders into.
   */
  const chevronWidth = chevron && onRowPress ? 18 + columnGap : 0;
  const gutters = metrics.gutter * 2;

  const widths = useMemo(
    () => resolveTrackWidths(tracks, Math.max(0, available - gutters - chevronWidth), columnGap),
    [tracks, available, gutters, chevronWidth, columnGap],
  );

  const isPressable = (row: Row): boolean =>
    !!onRowPress && (rowPressable ? rowPressable(row) : true);

  /* ── Stacked (narrow viewport) ─────────────────────────────────────────── */
  if (stackTables) {
    if (rows.length === 0) return <>{empty}</>;
    const [titleColumn, ...restColumns] = columns;
    return (
      <View style={styles.stack}>
        {rows.map((row, index) => (
          <StackedRow
            key={keyExtractor(row, index)}
            title={titleColumn?.render(row, index)}
            fields={restColumns
              .filter((column) => !column.hideWhenStacked)
              .map((column) => ({
                label: column.stackedLabel ?? column.header,
                content: column.render(row, index),
              }))}
            onPress={isPressable(row) ? () => onRowPress?.(row, index) : undefined}
            chevron={chevron}
          />
        ))}
      </View>
    );
  }

  /* ── Table (desktop) ───────────────────────────────────────────────────── */
  return (
    <View onLayout={onLayout}>
      {!hideHeader && (
      <View
        style={[
          styles.header,
          {
            paddingVertical: metrics.headerPaddingY,
            paddingHorizontal: metrics.gutter,
            gap: columnGap,
          },
        ]}
      >
        {columns.map((column, index) => (
          <View
            key={column.key}
            style={{
              width: widths[index] ?? 0,
              alignItems:
                aligns[index] === 'right'
                  ? 'flex-end'
                  : aligns[index] === 'center'
                    ? 'center'
                    : 'flex-start',
              minWidth: 0,
            }}
          >
            <Text numberOfLines={1} style={textRole.tableHeader}>
              {column.header}
            </Text>
          </View>
        ))}
        {chevronWidth > 0 && <View style={{ width: 18 }} />}
      </View>
      )}

      {rows.length === 0
        ? empty
        : rows.map((row, index) => (
            <TableRow
              key={keyExtractor(row, index)}
              widths={widths}
              aligns={aligns}
              cells={columns.map((column) => column.render(row, index))}
              onPress={isPressable(row) ? () => onRowPress?.(row, index) : undefined}
              chevron={chevron}
              paddingY={metrics.paddingY}
              gutter={metrics.gutter}
              columnGap={columnGap}
              divided={index < rows.length - 1}
            />
          ))}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: neutral.surface2,
    borderBottomWidth: 1,
    borderBottomColor: neutral.border,
  },
  stack: { gap: 10 },
});

export default DataTable;
export type { Column };
